
import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import InputField from '../common/InputField';
import Loader from '../common/Loader/Loader';
import { toastMessage } from '@/utils/toast-message';
import { createFormFiled, updateFormFiled, getColumnById } from '@/actions/fetch-form-fields';
import { formFieldsData, OptionType } from '../../types/settings/FormFieldProps';
import SelectOptions from '../common/SelectOptions/SelectOptions';
import { ExtendedFormData } from '../common/SelectOptions/types';
import {
  FormInput,
  Save,
  Loader2,
  Settings
} from 'lucide-react';

// Define the component props
interface CreateNewFieldFormProps {
  onClose: () => void;
  uuid?: string;
  initialData?: formFieldsData | null;
  onSuccess?: (formField: formFieldsData) => void;
}

// Define the form schema (removed module requirement)
const formFieldsSchema = z.object({
  col_type: z.string().min(1, 'Type is required'),
  col_label: z.string().min(1, 'Label is required'),
  col_description: z.string().optional(),
  col_required: z.string(),
  col_readonly: z.string(),
  col_active: z.string(),
  col_order: z.union([z.string(), z.number()]).optional().transform((val) => typeof val === 'string' ? Number(val) : (val || 0)),
  options: z.array(z.object({
    label: z.string().min(1, 'Option label is required'),
    active: z.boolean().optional().default(true),
    qualified: z.boolean().optional().default(false),
    is_won: z.boolean().optional().default(false),
    uuid: z.string().optional(),
    opt_order: z.number().optional()
  })).optional()
}).refine(
  (data) => {
    if (data.col_type === 'select') {
      return data.options && data.options.length > 0;
    }
    return true;
  },
  {
    message: "At least one option is required for select type",
    path: ["options"],
  }
);

// Helper function to convert options
// const convertOptionsToFormStructure = (options?: OptionType[]) => {
//   if (!options || !Array.isArray(options)) return [];

//   return options.map(opt => ({
//     uuid: opt.uuid || '',
//     label: opt.opt_name || opt.label || '',
//     value: opt.opt_value || opt.value || '',
//     active: opt.opt_active !== undefined
//       ? (typeof opt.opt_active === 'string'
//         ? (opt.opt_active === 'true' || opt.opt_active === 'yes')
//         : Boolean(opt.opt_active))
//       : true,
//     opt_order: opt.opt_order || 0
//   })).sort((a, b) => (a.opt_order || 0) - (b.opt_order || 0));
// };

// 14-05

const convertOptionsToFormStructure = (options?: OptionType[]) => {
  if (!options || !Array.isArray(options)) return [];

  return options.map(opt => ({
    uuid: opt.uuid || '',
    label: opt.opt_name || opt.label || '',
    value: opt.opt_value || opt.value || '',
    active: opt.opt_active !== undefined
      ? (typeof opt.opt_active === 'string'
        ? (opt.opt_active === 'true' || opt.opt_active === 'yes')
        : Boolean(opt.opt_active))
      : true,
    // API returns opt_qualified, so check both opt_qualified and qualified for compatibility
    qualified: (opt.opt_qualified !== undefined || opt.qualified !== undefined)
      ? (typeof (opt.opt_qualified ?? opt.qualified) === 'string'
        ? ((opt.opt_qualified ?? opt.qualified) === 'true' || (opt.opt_qualified ?? opt.qualified) === 'yes')
        : Boolean(opt.opt_qualified ?? opt.qualified))
      : false,
    is_won: opt.opt_is_won !== undefined
      ? (typeof opt.opt_is_won === 'string'
        ? (opt.opt_is_won === 'true' || opt.opt_is_won === 'yes')
        : Boolean(opt.opt_is_won))
      : false,
    opt_order: opt.opt_order || 0
  })).sort((a, b) => (a.opt_order || 0) - (b.opt_order || 0));
};

const CreateNewFieldForm: React.FC<CreateNewFieldFormProps> = ({
  onClose,
  uuid,
  initialData,
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!uuid);
  const [isStatusField, setIsStatusField] = useState(false);
  const isUpdateMode = !!uuid;

  // Prepare default values with a more robust structure (removed module fields)
  const defaultFormValues: ExtendedFormData = {
    col_type: 'text',
    col_label: '',
    col_description: '',
    col_required: 'no',
    col_readonly: 'no',
    col_active: 'yes',
    col_order: 0,
    options: []
  };

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm<ExtendedFormData>({
    resolver: zodResolver(formFieldsSchema),
    defaultValues: defaultFormValues
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "options"
  });

  const selectedType = watch('col_type');

  // Fetch column data when in edit mode
  useEffect(() => {
    const fetchColumnData = async () => {
      if (isUpdateMode && uuid) {
        try {
          setIsLoading(true);
          const response = await getColumnById(uuid);

          if (response.status === 'success') {
            const columnData = response.data;

            // Check if this is a status field
            setIsStatusField(columnData.col_name === 'status');

            // Prepare form values with fallbacks (removed module fields)
            const formValues: ExtendedFormData = {
              col_type: columnData.col_type || 'text',
              col_label: columnData.col_label || '',
              col_description: columnData.col_description || '',
              col_required: columnData.col_required ? 'yes' : 'no',
              col_readonly: columnData.col_readonly ? 'yes' : 'no',
              col_active: columnData.col_active ? 'yes' : 'no',
              col_order: columnData.col_order || 0,
              options: convertOptionsToFormStructure(columnData.options)
            };

            // Reset the entire form with the new values
            reset(formValues);
          } else {
            toastMessage('Failed to load column data', 'error');
          }
        } catch (error) {
          console.error('Error fetching column data:', error);
          toastMessage('Failed to load column data', 'error');
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchColumnData();
  }, [uuid, isUpdateMode, reset]);

  // Handle initial data (useful for pre-populated data)
  useEffect(() => {
    if (initialData) {
      // Check if this is a status field
      setIsStatusField(initialData.col_name === 'status');

      const formValues: ExtendedFormData = {
        col_type: initialData.col_type || 'text',
        col_label: initialData.col_label || '',
        col_description: initialData.col_description || '',
        col_required: initialData.col_required ? 'yes' : 'no',
        col_readonly: initialData.col_readonly ? 'yes' : 'no',
        col_active: initialData.col_active ? 'yes' : 'no',
        col_order: initialData.col_order || 0,
        options: convertOptionsToFormStructure(initialData.options)
      };

      reset(formValues);
    }
  }, [initialData, reset]);

  // Handle type-specific option management
  useEffect(() => {
    if (selectedType === 'select') {
      // Ensure at least one option exists for select type
      if (fields.length === 0) {
        append({
          label: 'Option 1',
          value: 'option_1',
          active: true,
          qualified: false,
          is_won: false,
          opt_order: 1
        });
      }
    } else {
      // Clear options for non-select types
      setValue('options', []);
    }
  }, [selectedType, fields.length, append, setValue]);

  // Input type options
  const inputTypeOptions = [
    { value: 'text', label: 'Text' },
    { value: 'select', label: 'Select' },
    { value: 'date', label: 'Date' },
    { value: 'dateTime', label: 'Date Time' }
  ];

  const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];


  // Custom add option function for select type
  const handleAddOption = () => {
    const currentOptions = fields || [];
    const maxOrder = currentOptions.reduce(
      (max, opt) => Math.max(max, (opt.opt_order || 0)),
      0
    );

    append({
      label: '',
      value: '',
      active: true,
      qualified: false,
      is_won: false,
      opt_order: maxOrder + 1
    });
  };

  // 14-05 

  // Form submission handler (to be placed in CreateNewFieldForm component)
  const onSubmit = async (formData: ExtendedFormData) => {
    try {
      setIsSubmitting(true);

      const apiData = {
        uuid: uuid || '',
        col_label: formData.col_label,
        col_type: formData.col_type,
        col_order: Number(formData.col_order) || 0,
        col_required: formData.col_required === 'yes',
        col_readonly: formData.col_readonly === 'yes',
        col_active: formData.col_active === 'yes',
        col_description: formData.col_description || '',

        options: (formData.col_type === 'select' && Array.isArray(formData.options)) ?
          formData.options.map((opt, index) => ({
            uuid: opt.uuid || '',
            opt_name: opt.label,
            label: opt.label,
            value: opt.value || opt.label.toLowerCase().replace(/\s+/g, '_'),
            opt_active: typeof opt.active === 'boolean'
              ? opt.active
              : opt.active === 'true' || opt.active === 'yes',
            opt_qualified: typeof opt.qualified === 'boolean'
              ? opt.qualified
              : opt.qualified === 'true' || opt.qualified === 'yes',
            opt_is_won: typeof opt.is_won === 'boolean'
              ? opt.is_won
              : opt.is_won === 'true' || opt.is_won === 'yes',
            opt_order: opt.opt_order || index + 1,
          })) : []
      };

      // Send API request
      let response;
      if (isUpdateMode && uuid) {
        response = await updateFormFiled(uuid, apiData);
      } else {
        response = await createFormFiled(apiData);
      }

      if (response.status === 'success') {
        toastMessage(`Form field ${isUpdateMode ? 'updated' : 'created'} successfully`, 'success');
        onSuccess?.(response.data as formFieldsData);
        onClose?.();
      } else {
        // Handle error response
        const errorMessage = response.message ||
          `Failed to ${isUpdateMode ? 'update' : 'create'} form field`;
        toastMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Submission Error:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to save form field';
      toastMessage(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader text="Loading field data..." withBackdrop={true} />
      </div>
    );
  }

  // Submitting state
  if (isSubmitting) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader text={isUpdateMode ? 'Updating Field...' : 'Creating Field...'} withBackdrop={true} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col min-h-0">
        <div className={`flex-1 overflow-y-auto p-6 transition-opacity ${isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
          <div className="space-y-8 max-w-4xl">
            
            {/* Basic Field Information */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
                <FormInput className="w-4 h-4 mr-2" />
                Field Configuration
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <InputField
                    label="Field Label"
                    id="col_label"
                    name="col_label"
                    type="text"
                    placeholder="Enter field label (e.g., Full Name, Email)"
                    control={control}
                    error={errors.col_label?.message}
                    required
                  />
                </div>

                <div>
                  <InputField
                    label="Field Type"
                    id="col_type"
                    name="col_type"
                    type="select"
                    options={inputTypeOptions}
                    control={control}
                    error={errors.col_type?.message}
                    required
                    disabled={isUpdateMode}
                  />
                </div>

                <div>
                  <InputField
                    label="Display Order"
                    id="col_order"
                    name="col_order"
                    type="number"
                    placeholder="e.g., 1, 2, 3..."
                    control={control}
                    error={errors.col_order?.message}
                  />
                </div>
              </div>

              <div>
                <InputField
                  label="Description"
                  id="col_description"
                  name="col_description"
                  type="text"
                  placeholder="Brief description of what this field captures"
                  control={control}
                  error={errors.col_description?.message}
                />
              </div>
            </div>

            {/* Select Options */}
            {selectedType === 'select' && (
              <div className="space-y-4">
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Dropdown Options
                  </h3>
                  <SelectOptions
                    selectedType={selectedType}
                    fields={fields}
                    control={control}
                    errors={errors}
                    append={handleAddOption}
                    remove={remove}
                    move={move}
                    setValue={setValue}
                    showQualifiedCheckbox={true}
                    showIsWonCheckbox={isStatusField}
                  />
                </div>
              </div>
            )}

            {/* Field Properties */}
            <div className="space-y-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Field Properties
              </h3>
              
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-900 dark:text-white">Required Field</label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Users must fill this field to submit the form</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <InputField
                        id="col_required"
                        name="col_required"
                        type="radio"
                        className="flex gap-4 items-center"
                        radioOptions={yesNoOptions}
                        control={control}
                        error={errors.col_required?.message}
                        required
                        hideLabel
                      />
                    </div>
                  </div>

                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-900 dark:text-white">Read Only</label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Field is visible but cannot be edited by users</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <InputField
                        id="col_readonly"
                        name="col_readonly"
                        type="radio"
                        className="flex gap-4 items-center"
                        radioOptions={yesNoOptions}
                        control={control}
                        error={errors.col_readonly?.message}
                        required
                        hideLabel
                      />
                    </div>
                  </div>

                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-900 dark:text-white">Active</label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Show this field in forms and data collection</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <InputField
                        id="col_active"
                        name="col_active"
                        type="radio"
                        className="flex gap-4 items-center"
                        radioOptions={yesNoOptions}
                        control={control}
                        error={errors.col_active?.message}
                        required
                        hideLabel
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer Actions - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-end space-x-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor border border-transparent rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isUpdateMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isUpdateMode ? 'Update Field' : 'Create Field'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateNewFieldForm;